import type { ProductAnalysisPage, ProductAnalysisSort } from "./productAnalysisModel";

export type InitialProductPage = {
  sort: ProductAnalysisSort;
  status: string;
  request: Promise<ProductAnalysisPage & { release_id: string }>;
};

// One bounded first-page read for the current report. There is no settled-data
// cache: a new report starts a new request and cancels the preceding one.
export class ProductPagePrefetch {
  private current?: { page: InitialProductPage; controller: AbortController };
  start(sort: ProductAnalysisSort, status: string, load: (signal: AbortSignal) => InitialProductPage["request"]): InitialProductPage {
    this.cancel();
    const controller = new AbortController();
    const request = load(controller.signal);
    // The main report may fail before the colour table mounts. Keep the same
    // rejected promise for its consumer without an unhandled rejection.
    void request.catch(() => undefined);
    const page = { sort, status, request };
    this.current = { page, controller };
    return page;
  }
  cancel(page?: InitialProductPage) {
    if (page && this.current?.page !== page) return;
    this.current?.controller.abort();
    this.current = undefined;
  }
}

export function matchesInitialProductPage(page: InitialProductPage | undefined, sort: ProductAnalysisSort, status: string): page is InitialProductPage {
  return Boolean(page && page.status === status && (page.sort?.key ?? null) === (sort?.key ?? null) && (page.sort?.direction ?? null) === (sort?.direction ?? null));
}

export async function readInitialProductPage(page: InitialProductPage, releaseId: string): Promise<ProductAnalysisPage> {
  const result = await page.request;
  if (result.release_id !== releaseId) throw new Error("Veri paketi değişti. Sayfayı yenileyin.");
  return result;
}

// With only the edited filter selected, catalog normalization cannot remove
// any other selection. All dependent selections retain the sequential path.
export function canOverlapFilterCatalog(filters: Record<string, string[]>, edited: string): boolean {
  return Object.entries(filters).every(([key, values]) => key === edited || values.length === 0);
}
